import React, { FC, ReactNode, createContext, useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { getBetaWhitelist } from "./beta/apis/getBetaWhitelist";
import { getAccount } from "./main/apis/get-account";
import { getFeatureFlags } from "./main/apis/get-featureflags";
import { getLocale } from "./main/apis/get-locale";
import { setAccount as setAccountApi } from "./main/apis/set-account";
import { FeatureFlagsResponse } from "./main/models/feature-flags-response";
import { GetAccountResponse } from "./main/models/get-account-response";
import { SelectAccountResponse } from "./main/models/select-account-response";

interface AppContextState {
  account?: GetAccountResponse;
  selectAccount: (accountId: string, isExternal: boolean) => Promise<SelectAccountResponse>;
  locale: string;
  setLocale: (locale: string) => void;
  featureFlags?: FeatureFlagsResponse;
  betaWhitelist?: boolean;
}

const selectAccount = async (accountId: string, isExternal: boolean): Promise<SelectAccountResponse> => {
  const response = await setAccountApi(accountId, isExternal);

  if (response.code === 200) {
    mutate("accounts/selected", undefined);
  }
  return response.body;
};

const initialConfig = {
  selectAccount: selectAccount,
  locale: "nb",
  setLocale: () => Promise.resolve(),
  featureFlags: {
    uxSignalsEnabled: false,
    useUxSignalsDemo: false,
    featureBetaModeEnabled: false
  },
  betaWhiteList: false
};

export const AppContext = createContext<AppContextState>(initialConfig);

export interface AppProviderProps {
  requireAccount?: boolean;
  children?: ReactNode;
}

export const AppProvider: FC<AppProviderProps> = ({ requireAccount = true, children }) => {
  const { data } = useSWR(requireAccount ? "accounts/selected" : null, getAccount, { shouldRetryOnError: false });

  useEffect(() => {
    getLocale().then((locale) => {
      setLocale(locale);
    });
  }, []);

  const [locale, setLocale] = useState<string>(initialConfig.locale);

  const { data: featureFlags } = useSWR("featureflags", getFeatureFlags);
  const { data: betaWhiteList } = useSWR("beta-whitelist", getBetaWhitelist);

  return (
    <AppContext.Provider
      value={{
        ...initialConfig,
        account: data,
        selectAccount,
        locale: locale,
        setLocale: setLocale,
        featureFlags: featureFlags,
        betaWhitelist: betaWhiteList
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
